import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { createService, updateService, fetchWatches, fetchValidation, setServicePhotos } from "@/lib/api";
import { toast } from "sonner";
import { errorMessage } from "@/lib/errors";
import ServicePhotos from "@/components/service/ServicePhotos";
import PastCaliberPhotos from "@/components/service/PastCaliberPhotos";

// Fallbacks used until the user-managed lists arrive from /api/validation.
// (Also used to seed the Dropdown Manager on first launch.)
const FALLBACK_SERVICE_TYPES = [
  "Overhaul", "Cleaning", "Oiling", "Regulation", "Demagnetize",
  "Mainspring replacement", "Balance staff replacement", "Jewel replacement",
  "Crystal replacement", "Case polish", "Other",
];
const FALLBACK_SERVICE_STATUSES = ["Logged", "In Progress", "Complete"];

const EMPTY = {
  service_date: "", service_type: "", description: "",
  performed_by: "", cost: "", parts_used: "", notes: "",
  status: "Logged",
};

const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
const str = (v) => (v === "" || v === null || v === undefined ? null : String(v));

export default function ServiceFormSheet({ open, onOpenChange, service, watchId, onSaved }) {
  const [data, setData] = useState(EMPTY);
  const [photos, setPhotos] = useState([]);
  const [photosDirty, setPhotosDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  // Dropdown options from the user-managed Validation collection. Falls
  // back to in-code defaults until the network call resolves.
  const [serviceTypes, setServiceTypes] = useState(FALLBACK_SERVICE_TYPES);
  const [serviceStatuses, setServiceStatuses] = useState(FALLBACK_SERVICE_STATUSES);
  // When no watchId is supplied (e.g. opened from the Workshop page),
  // let the user pick which watch this service is for. The combobox is
  // a label-string control, so we keep a label→id map alongside.
  const [pickedWatchLabel, setPickedWatchLabel] = useState("");
  const [watchOptions, setWatchOptions] = useState([]); // [{id, label}]
  const needsWatchPicker = !watchId && !service?.watch_id;

  useEffect(() => {
    if (!open) return;
    fetchValidation()
      .then((v) => {
        const t = v?.["Service Type"];
        const s = v?.["Service Status"];
        if (Array.isArray(t) && t.length) setServiceTypes(t.map(String));
        if (Array.isArray(s) && s.length) setServiceStatuses(s.map(String));
      })
      .catch(() => {});
  }, [open]);

  useEffect(() => {
    if (!open || !needsWatchPicker) return;
    fetchWatches({ limit: 1000, sort: "excel_id", order: "asc" })
      .then((r) => {
        const opts = (r.items || []).map((w) => ({
          id: w.id,
          label: `#${w.excel_id ?? "—"} · ${w.brand || "Unbranded"}${w.model ? " " + w.model : ""}${w.caliber ? " · " + w.caliber : ""}`,
        }));
        setWatchOptions(opts);
      })
      .catch(() => setWatchOptions([]));
  }, [open, needsWatchPicker]);

  useEffect(() => {
    setPhotos(service?.photos || []);
    setPhotosDirty(false);
    if (service) {
      const seed = { ...EMPTY };
      Object.keys(EMPTY).forEach((k) => {
        seed[k] = service[k] === null || service[k] === undefined ? "" : service[k];
      });
      setData(seed);
      setPickedWatchLabel("");
    } else {
      setData({
        ...EMPTY,
        service_date: new Date().toISOString().slice(0, 10),
        performed_by: "Self",
      });
      setPickedWatchLabel("");
    }
  }, [service, open, watchId]);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  // Existing services save photo changes immediately (safe if the sheet is closed
  // without saving); new services send them with the create call.
  const changePhotos = async (next) => {
    setPhotos(next);
    if (!service?.id) return;
    try {
      await setServicePhotos(service.id, next);
      setPhotosDirty(true);
    } catch (err) {
      toast.error(errorMessage(err, "Could not save photos"));
    }
  };

  const handleOpenChange = (o) => {
    if (!o && photosDirty) { setPhotosDirty(false); onSaved?.(); return; }
    onOpenChange(o);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let targetWatchId = watchId || service?.watch_id;
    if (!targetWatchId && needsWatchPicker) {
      const match = watchOptions.find((o) => o.label === pickedWatchLabel);
      targetWatchId = match?.id || null;
    }
    if (!targetWatchId) {
      toast.error("Pick a watch for this service");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        watch_id: targetWatchId,
        service_date: str(data.service_date),
        service_type: str(data.service_type),
        description: str(data.description),
        performed_by: str(data.performed_by),
        cost: num(data.cost),
        parts_used: str(data.parts_used),
        notes: str(data.notes),
        status: str(data.status) || "Logged",
        photos,
      };
      if (service?.id) {
        await updateService(service.id, payload);
        toast.success("Service updated");
      } else {
        await createService(payload);
        toast.success("Service logged");
      }
      onSaved?.();
    } catch (err) {
      toast.error(errorMessage(err, "Failed to save"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-lg bg-[#FBF3D9] border-l border-[#8B6A1F]/45 text-[#0A0604] overflow-y-auto pb-10" data-testid="service-form-sheet">
        <SheetHeader className="mb-6">
          <SheetTitle className="font-serif-display text-3xl">{service ? "Edit Service" : "Log Service"}</SheetTitle>
          <SheetDescription className="text-[#3A2A10] text-xs uppercase tracking-[0.25em]">Workshop entry</SheetDescription>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="space-y-5" data-testid="service-form">
          {needsWatchPicker && (
            <Field label="Watch">
              <ComboSelect
                value={pickedWatchLabel}
                onChange={setPickedWatchLabel}
                options={watchOptions.map((o) => o.label)}
                testid="svc-form-watch"
              />
              <div className="text-[10px] text-[#3A2A10] mt-1">
                Pick which watch this service is for. {watchOptions.length} available.
              </div>
            </Field>
          )}
          <Row2>
            <Field label="Date">
              <Input type="date" value={data.service_date || ""} onChange={(e) => set("service_date", e.target.value)} className="dark-input" data-testid="svc-form-date" />
            </Field>
            <Field label="Type">
              <ComboSelect
                value={data.service_type} onChange={(v) => set("service_type", v)}
                options={serviceTypes} testid="svc-form-type"
              />
            </Field>
          </Row2>
          <Field label="Status">
            <ComboSelect
              value={data.status} onChange={(v) => set("status", v)}
              options={serviceStatuses} testid="svc-form-status"
            />
            <div className="text-[10px] text-[#3A2A10] mt-1">
              Setting to <span className="font-mono-data">In Progress</span> flips the watch to <span className="font-mono-data">Being Serviced</span>.
              Use the <span className="font-mono-data">Complete</span> button on the table row to close and pick a new watch status.
            </div>
          </Field>
          <Field label="Description">
            <Textarea value={data.description} onChange={(e) => set("description", e.target.value)} className="dark-input min-h-40 resize-y" data-testid="svc-form-description" placeholder="What was done, procedure details…" />
          </Field>
          <Row2>
            <Field label="Performed by">
              <Input value={data.performed_by} onChange={(e) => set("performed_by", e.target.value)} className="dark-input" data-testid="svc-form-by" placeholder="Self / Watchmaker name" />
            </Field>
            <Field label="Cost">
              <Input type="number" step="0.01" value={data.cost} onChange={(e) => set("cost", e.target.value)} className="dark-input" data-testid="svc-form-cost" />
            </Field>
          </Row2>
          <Field label="Parts used">
            <Input value={data.parts_used} onChange={(e) => set("parts_used", e.target.value)} className="dark-input" data-testid="svc-form-parts" placeholder="Mainspring, crystal, etc." />
          </Field>
          <Field label="Notes">
            <Textarea value={data.notes} onChange={(e) => set("notes", e.target.value)} className="dark-input min-h-32 resize-y" data-testid="svc-form-notes" />
          </Field>

          <ServicePhotos photos={photos} onChange={changePhotos} />
          <PastCaliberPhotos
            watchId={watchId || service?.watch_id || watchOptions.find((o) => o.label === pickedWatchLabel)?.id}
            serviceId={service?.id}
          />

          <div className="flex items-center justify-end gap-2 pt-4 border-t border-[#8B6A1F]/35">
            <Button type="button" variant="ghost" onClick={() => handleOpenChange(false)} className="text-[#3A2A10] hover:text-[#0A0604]" data-testid="svc-form-cancel">Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-[#D4AF37] hover:bg-[#E5C158] text-[#0A0C10] uppercase tracking-wider text-xs rounded-sm" data-testid="svc-form-submit">
              {saving ? "Saving…" : service ? "Save Changes" : "Log Service"}
            </Button>
          </div>
        </form>

        <style>{`.dark-input{background-color: #F9F0D4 !important;border-color: rgba(139,106,31,0.25) !important;border-radius:2px!important;color: #2B2214 !important}.dark-input:focus,.dark-input:focus-visible{border-color:rgba(212,175,55,0.5)!important;outline:none!important;box-shadow:0 0 0 1px rgba(212,175,55,0.3)!important}`}</style>
      </SheetContent>
    </Sheet>
  );
}

function Row2({ children }) { return <div className="grid grid-cols-2 gap-4">{children}</div>; }
function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[10px] uppercase tracking-[0.2em] text-[#3A2A10] font-normal">{label}</Label>
      {children}
    </div>
  );
}
function DatalistInput({ value, onChange, options, listId, testid }) {
  return (
    <>
      <Input value={value || ""} onChange={(e) => onChange(e.target.value)} className="dark-input" list={listId} data-testid={testid} />
      <datalist id={listId}>
        {options.map((o) => <option key={o} value={o} />)}
      </datalist>
    </>
  );
}

import SearchableCombobox from "@/components/SearchableCombobox";

function ComboSelect({ value, onChange, options = [], testid }) {
  return (
    <SearchableCombobox value={value} onChange={onChange} options={options} testid={testid} />
  );
}
